import api from './client';

export interface ConsentRecord {
  id: string;
  phone: string;
  consent_method: string;
  consent_text?: string;
  recording_url?: string;
  ip_address?: string;
  consented_at: string;
  expires_at: string;
  revoked_at?: string;
}

export interface DncEntry {
  id: string;
  phone: string;
  reason: string;
  added_at: string;
}

export interface ComplianceEvent {
  id: string;
  event_type: string;
  phone?: string;
  campaign_id?: string;
  call_sid?: string;
  details: Record<string, any>;
  created_at: string;
}

export interface ComplianceStats {
  totalConsentRecords: number;
  dncListSize: number;
  callsBlockedThisMonth: number;
  complianceScore: number;
}

export interface PreCallResult {
  approved: boolean;
  reasons: string[];
  warnings: string[];
}

// Stats
export async function getComplianceStats(): Promise<ComplianceStats> {
  const res = await api.get('/api/v1/compliance/stats');
  return res.data.data;
}

// Consent
export async function getConsentRecords(page = 1, limit = 50): Promise<{ records: ConsentRecord[]; total: number }> {
  const res = await api.get('/api/v1/compliance/consent', { params: { page, limit } });
  return { records: res.data.data, total: res.data.meta?.total ?? 0 };
}

export async function checkConsent(phone: string): Promise<{
  hasConsent: boolean;
  consentDate?: string;
  method?: string;
  expiresAt?: string;
}> {
  const res = await api.get(`/api/v1/compliance/consent/${encodeURIComponent(phone)}`);
  return res.data.data;
}

export async function addConsent(data: {
  phone: string;
  consentMethod: 'web_form' | 'text' | 'verbal' | 'import';
  consentText?: string;
  recordingUrl?: string;
}): Promise<{ consentId: string; phone: string; valid: boolean }> {
  const res = await api.post('/api/v1/compliance/consent', data);
  return res.data.data;
}

export async function revokeConsent(phone: string): Promise<void> {
  await api.delete(`/api/v1/compliance/consent/${encodeURIComponent(phone)}`);
}

// DNC
export async function getDncList(page = 1, limit = 50): Promise<{ entries: DncEntry[]; total: number }> {
  const res = await api.get('/api/v1/compliance/dnc', { params: { page, limit } });
  return { entries: res.data.data, total: res.data.meta?.total ?? 0 };
}

export async function addToDNC(phone: string, reason: string): Promise<void> {
  await api.post('/api/v1/compliance/dnc/add', { phone, reason });
}

export async function checkDNC(phones: string[]): Promise<Array<{ phone: string; onDNC: boolean; reason?: string }>> {
  const res = await api.post('/api/v1/compliance/dnc/check', { phones });
  return res.data.data;
}

// Audit log
export async function getAuditLog(params: {
  page?: number;
  limit?: number;
  eventType?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{ events: ComplianceEvent[]; total: number }> {
  const res = await api.get('/api/v1/compliance/audit-log', { params });
  return { events: res.data.data, total: res.data.meta?.total ?? 0 };
}

// Pre-call check
export async function preCallCheck(phone: string, campaignId?: string): Promise<PreCallResult> {
  const res = await api.post('/api/v1/compliance/check-call', { phone, campaignId });
  return res.data.data;
}
